import { createSelector } from 'reselect';
import { Platform } from 'react-native';
import { selectActiveComponentIndex } from './search.selector';
import { hoteBrandMap } from '../../constants/BrandMaps';
import { orderByHotelList } from '../../constants/orderByLists';
import { orderByAscWithParse, orderByDescWithParse } from '../../utils/filter.utils';
import { convertHotelDataToSegment, ALL } from '../../utils/hotel.utils';
import { selectCompanyCurrency, selectCompanyCurrencyCulture } from './auth.selector';

export const selectHotelFeatureState = (state) => state.hotel;

export const selectHotelComponents = createSelector(
  selectHotelFeatureState,
  (state) => state && state.components
);

export const selectActiveHotelComponent = createSelector(
  selectHotelComponents,
  selectActiveComponentIndex,
  (state, displayIndex) => state && state[displayIndex]
);

export const selectHotelSearchResults = createSelector(
  selectActiveHotelComponent,
  (state) => state?.searchResults
);

export const selectedHotelComponents = createSelector(selectHotelComponents, (state) => {
  const keys = Object.keys(state);
  const selectedHotels = [];
  keys.map((res) => {
    selectedHotels.push(state[res].selectedHotel);
  });
  return state && selectedHotels;
});

export const selectNeighbourhoodListWithHotelIds = createSelector(
  selectActiveHotelComponent,
  (state) => {
    const neiborhood = state?.HotelFilterSummaryData?.NeighbourhoodListWithHotelIds;
    if (!neiborhood) {
      return null;
    }

    return neiborhood.reduce((acum, value) => {
      if (acum[value.HotelId]) {
        acum[value.HotelId].push(value.RegionId);
      } else {
        acum[value.HotelId] = [value.RegionId];
      }
    }, {});
  }
);

export const selectSelectedHotel = createSelector(
  selectActiveHotelComponent,
  (state) => state?.selectedHotel
);

export const selectHotelDetail = createSelector(
  selectActiveHotelComponent,
  (state) => state?.hotelDetail
);

export const selectHotelResponses = createSelector(
  selectHotelSearchResults,
  (state) => state && state.HotelListData
);

export const selectHotelSummary = createSelector(
  selectHotelSearchResults,
  (state) => state && state.HotelFilterSummaryData
);

export const selectNeighbourhoodList = createSelector(
  selectHotelSummary,
  (state) => state?.NeighbourhoodList
);

export const selectHotelDetailSliderPhotos = createSelector(selectHotelDetail, (state) => {
  if (state && state.LargeImageSliderPhotos) {
    const photos = state.LargeImageSliderPhotos.split('|');
    const limit = Platform.OS === 'web' ? 100 : 20; // 0 based limit
    const result = [];
    for (let i = 0; i < photos.length; i++) {
      if (!photos[i] || i > limit) {
        break;
      }
      result.push(photos[i].replace('http://', 'https://'));
    }
    return result;
  }
  return [];
});

export const selectHotelDetailAmenities = createSelector(selectHotelDetail, (hotelDetail) => {
  if (
    hotelDetail?.Amenities &&
    hotelDetail.Amenities.length > 0 &&
    hotelDetail.Amenities.indexOf(',') >= 0
  ) {
    //amenities is returned as a common seperated string, so we convert to array
    const arrayAmenities = hotelDetail.Amenities.split(',');
    //return only unique amenities
    return [...new Set(arrayAmenities)];
  }
  return [];
});

export const selectHotelBrands = createSelector(selectHotelResponses, (state) => {
  if (!state) {
    return state;
  }

  const prices = state.reduce(
    (acum, current) => {
      if (current.payNow) {
        acum.payNow = acum.payNow < current.payNow.price ? acum.payNow : current.payNow.price;
      }
      if (current.payLater) {
        acum.payLater =
          acum.payLater < current.payLater.price ? acum.payLater : current.payLater.price;
      }
      if (current.payNow?.isLowest) {
        acum.city = acum.city < current.payNow.price ? acum.city : current.payNow.price;
        if (current.AirportDistance <= 7) {
          acum.airport = acum.airport < current.payNow.price ? acum.airport : current.payNow.price;
        }
      } else if (current.payLater?.isLowest) {
        acum.city = acum.city < current.payLater.price ? acum.city : current.payLater.price;
        if (current.AirportDistance <= 7) {
          acum.airport =
            acum.airport < current.payLater.price ? acum.airport : current.payLater.price;
        }
      }

      return acum;
    },
    {
      payNow: Infinity,
      payLater: Infinity,
      city: Infinity,
      airport: Infinity,
    }
  );

  return Object.entries(hoteBrandMap).map(([key, value]) => {
    return {
      ...value,
      brand: key,
      price: prices[key] === Infinity ? 0 : prices[key],
    };
  });
});

export const selectHotelSearchType = createSelector(
  selectActiveHotelComponent,
  (state) => state?.searchType
);

export const selectHotelFilterBy = createSelector(
  selectActiveHotelComponent,
  (state) => state?.filterBy
);

export const selectHotelOrderBy = createSelector(
  selectActiveHotelComponent,
  (state) => state?.orderBy
);

export const selectHotelFilterByName = createSelector(
  selectActiveHotelComponent,
  (state) => state?.filterByName
);

export const selectHotelOrderByReverse = createSelector(
  selectActiveHotelComponent,
  (state) => state?.reverse
);

export const selectHotelListFiltered = createSelector(
  selectHotelResponses,
  selectHotelSearchType,
  (state, searchType) => {
    if (!state || !searchType) {
      return [];
    }

    let res = [];
    switch (searchType) {
      case 'city':
        res = state
          .map((hotel) => {
            if (hotel?.payNow?.isLowest) {
              return {
                ...hotel,
                ...hotel.payNow,
              };
            } else if (hotel?.payLater?.isLowest) {
              return {
                ...hotel,
                ...hotel.payLater,
              };
            }
          })
          .filter((h) => !!h);
        break;
      case 'airport':
        res = state
          .filter(({ AirportDistance }) => {
            return AirportDistance <= 7;
          })
          .map((hotel) => {
            if (hotel?.payNow?.isLowest) {
              return {
                ...hotel,
                ...hotel.payNow,
                distance: hotel.AirportDistance,
              };
            } else if (hotel?.payLater?.isLowest) {
              return {
                ...hotel,
                ...hotel.payLater,
                distance: hotel.AirportDistance,
              };
            }
          })
          .filter((h) => !!h);
        break;
      case 'payNow':
        res = state
          .filter((hotel) => {
            return !!hotel.payNow;
          })
          .map((hotel) => {
            return {
              ...hotel,
              ...hotel.payNow,
            };
          });
        break;
      case 'payLater':
        res = state
          .filter((hotel) => {
            return !!hotel.payLater;
          })
          .map((hotel) => {
            return {
              ...hotel,
              ...hotel.payLater,
            };
          });
        break;
    }

    return res;
  }
);

export const selectHotelFilterByDetails = createSelector(
  selectActiveHotelComponent,
  (state) => state?.filterByDetails
);

export const selectHotelFilteredByName = createSelector(
  selectHotelListFiltered,
  selectHotelFilterByName,
  (state, name) => {
    if (!state || !name) {
      return state;
    }
    return state.filter((h) => h.HotelName.toLowerCase().includes(name.toLowerCase()));
  }
);

export const selectHotelFilteredWithDetails = createSelector(
  selectHotelFilteredByName,
  selectHotelFilterByDetails,
  (state, filterByDetails) => {
    if (!state) {
      return [];
    }

    if (state && filterByDetails) {
      let results = state;
      const star = filterByDetails?.star;
      const company = filterByDetails?.neiborhood?.group?.items;
      const isModified = star && Object.values(star).some((s) => !s.checked);

      if (isModified) {
        results = state.filter(({ StarRating, HotelCode }) => {
          if (!star[StarRating]) {
            return false;
          }
          if (star[StarRating].checked) {
            return true;
          }
          if (star[StarRating].items[HotelCode]) {
            return star[StarRating].items[HotelCode].checked;
          } else if (star[StarRating].items.other) {
            return star[StarRating].items.other.checked;
          }

          return false;
        });
      }

      if (company) {
        if (Object.values(company).some((c) => !c.checked)) {
          results = results.filter(({ regionIdList }) => {
            if (!regionIdList) {
              return false;
            }
            return regionIdList.some((id) => company[id]?.checked);
          });
        }
      }

      return results;
    }
  }
);

export const selectConvertedHotelMatrixData = createSelector(
  selectHotelFilteredWithDetails,
  selectCompanyCurrency,
  selectCompanyCurrencyCulture,
  (state, currency, currencyCulture) => {
    return convertHotelDataToSegment(state, currency, currencyCulture);
  }
);

export const selectHotelsFilteredBy = createSelector(
  selectHotelFilteredWithDetails,
  selectHotelFilterBy,
  (state, filterBy) => {
    if (!state || !filterBy || filterBy === ALL) {
      return state;
    }

    return state.filter((i) => i.StarRating.toString() === filterBy.toString());
  }
);

export const selectHotelsFiltered = createSelector(
  selectHotelsFilteredBy,
  selectHotelOrderBy,
  selectHotelOrderByReverse,
  (state, orderBy, reverse) => {
    if (state && orderBy) {
      let sortedResults = [];
      if (orderBy === orderByHotelList[0].value) {
        sortedResults = state.slice().sort((a, b) => orderByAscWithParse(a[orderBy], b[orderBy]));
      } else {
        if (reverse) {
          sortedResults = state.slice().sort((a, b) => orderByAscWithParse(a[orderBy], b[orderBy]));
        } else {
          sortedResults = state
            .slice()
            .sort((a, b) => orderByDescWithParse(a[orderBy], b[orderBy]));
        }
      }
      return sortedResults.sort((a, b) =>
        a.IsCorporateRate === b.IsCorporateRate ? 0 : a.IsCorporateRate ? -1 : 1
      );
    } else {
      return state;
    }
  }
);
export const selectedHotelDetail = createSelector(selectHotelComponents, (state) => {
  if (!state || typeof state !== 'object') return undefined;
  const matchingEntry = Object.values(state).find((entry) => entry?.selectedHotelDetail);
  const stateSelectedHotelDetail = matchingEntry?.selectedHotelDetail;
  return stateSelectedHotelDetail;
});
